/**
 * @fileoverview ProgressButton component for the Storyteller web application.
 * Displays a progress button with a progress indicator.
 *
 * @example
 * // Basic usage
 * <ProgressButton label="Progress" duration={1000} />
 */

import { useEffect, useMemo, useState } from 'react'
import { useProgress } from '../../hooks/useProgress'
import css from './ProgressButton.module.css'

/**
 * @interface ButtonProps
 * @description Props for the ProgressButton component.
 * @extends {React.HTMLAttributes<HTMLDivElement>}
 */
interface ButtonProps extends React.HTMLAttributes<HTMLDivElement> {
    label: string
    duration: number
}

/**
 * @function ProgressButton
 * @description Component for displaying a progress button with a progress indicator.
 * @param {ButtonProps} props - The props for the ProgressButton component.
 * @returns {JSX.Element} The ProgressButton component.
 */
export function ProgressButton({ label, duration, role }: ButtonProps) {
    const [canvas, setCanvas] = useState<HTMLCanvasElement>()
    const controls = useMemo(
        () =>
            useProgress({
                canvas,
                duration,
            }),
        [canvas]
    )

    function onRef(node: HTMLCanvasElement) {
        setCanvas(node)
    }

    useEffect(() => {
        if (canvas && controls) {
            controls.start()
        }
    }, [controls, canvas])

    return (
        <div role={role} className={css.container}>
            <canvas
                className={css.canvas}
                ref={onRef}
                width={130 * devicePixelRatio}
                height={130 * devicePixelRatio}
            />
            <button disabled className={css.button}>
                {label}
            </button>
        </div>
    )
}
